/**
 * Detect text direction based on character analysis
 * Auto-detects whether text is primarily RTL (Arabic/Hebrew) or LTR (English/Latin)
 * @param {string} text - The text to analyze
 * @returns {'rtl' | 'ltr'}
 */
export function detectTextDirection(text) {
  if (!text || text.trim().length === 0) {
    return "ltr"; // Default to LTR for empty text
  }

  // Arabic Unicode ranges
  const arabicRange =
    /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;

  // Hebrew Unicode range
  const hebrewRange = /[\u0590-\u05FF]/;

  // Count RTL and LTR characters
  let rtlCount = 0;
  let ltrCount = 0;

  for (let char of text) {
    if (arabicRange.test(char) || hebrewRange.test(char)) {
      rtlCount++;
    } else if (/[a-zA-Z]/.test(char)) {
      ltrCount++;
    }
  }

  // Return direction based on which has more characters
  // If equal or no letters detected, default to LTR
  return rtlCount > ltrCount ? "rtl" : "ltr";
}

/**
 * Apply text direction styling to an element
 * @param {string} text - The text to analyze
 * @returns {{dir: 'rtl' | 'ltr', textAlign: 'right' | 'left'}}
 */
export function getTextDirectionStyle(text) {
  const dir = detectTextDirection(text);
  return {
    dir,
    textAlign: dir === "rtl" ? "right" : "left",
  };
}

/**
 * Check if text contains mixed RTL and LTR content
 * @param {string} text - The text to analyze
 * @returns {boolean}
 */
export function hasMixedDirection(text) {
  if (!text || text.trim().length === 0) {
    return false;
  }

  const arabicRange =
    /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
  const hebrewRange = /[\u0590-\u05FF]/;
  const latinRange = /[a-zA-Z]/;

  const hasRTL = arabicRange.test(text) || hebrewRange.test(text);
  const hasLTR = latinRange.test(text);

  return hasRTL && hasLTR;
}
